import type { ComponentProps, ChangeEvent } from 'react';
import { useCallback, useMemo } from 'react';
import { Autocomplete, TextInput } from 'is24-corecss';
import { useController, useFormContext } from 'react-hook-form';
import { useCombobox } from 'downshift';
import type { AddressSuggestion } from '@/api/geoAutocomplete/geoAutocomplete';
import { useIntl } from '@/i18n';

export const AddressAutocomplete = ({
  name,
  label,
  className,
  disabled,
  suggestions,
  loading,
  lookupFailed,
  onSearch,
}: {
  name: 'city' | 'street';
  label: string;
  className: string;
  disabled: boolean;
  suggestions?: AddressSuggestion[];
  loading: boolean;
  lookupFailed: boolean;
  onSearch?: (value: string) => void;
}) => {
  const { t } = useIntl();
  const { control } = useFormContext();
  const {
    field,
    fieldState: { error },
  } = useController({
    control,
    name,
    rules: {
      required: t({ id: 'common.errorMessages.inputRequired' }),
      maxLength: 255,
    },
  });
  const updateAddress = useCallback((value: string) => field.onChange(value.trim()), [field.onChange]);
  const items = useMemo(
    () =>
      (suggestions ?? [])
        .filter(({ value }) => !!value.trim())
        .map(({ id, label, matches, value }) => ({
          label,
          matches,
          data: { id, value },
        })),
    [suggestions],
  );
  const preserveAddressInput: NonNullable<ComponentProps<typeof Autocomplete.Root>['stateReducer']> = (
    state,
    { type, changes },
  ) => {
    // Async suggestions can remove the selected item; keep the entered text.
    if (type === useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem) {
      return { ...changes, inputValue: state.inputValue };
    }
    const selected = changes.selectedItem;
    if (
      (type === useCombobox.stateChangeTypes.ItemClick ||
        type === useCombobox.stateChangeTypes.InputKeyDownEnter ||
        type === useCombobox.stateChangeTypes.FunctionSelectItem) &&
      typeof selected?.data?.value === 'string'
    ) {
      return {
        ...changes,
        selectedItem: { ...selected, label: selected.data.value },
        inputValue: selected.data.value,
      };
    }
    return changes;
  };
  const hasAddressText = (value: string) => !!value.trim();
  const selectAddress: NonNullable<ComponentProps<typeof Autocomplete.Root>['onSelect']> = (item) =>
    updateAddress(typeof item?.data?.value === 'string' ? item.data.value : (item?.label ?? ''));
  const enterAddress = (event: ChangeEvent<HTMLInputElement>) => updateAddress(event.target.value);

  const id = `adac-${name}`;
  const descriptionId = error ? `${id}-error-text` : lookupFailed ? `${id}-helper-text` : undefined;
  return (
    <TextInput.Root id={id} className={className} invalid={!!error} disabled={disabled}>
      <Autocomplete.Root
        items={items}
        loading={loading}
        debounceTime={300}
        onChange={onSearch}
        isInputValid={hasAddressText}
        onSelect={selectAddress}
        stateReducer={preserveAddressInput}
      >
        <Autocomplete.Input
          name={name}
          ref={field.ref}
          label={label}
          autoComplete='off'
          required
          disabled={disabled}
          invalid={!!error}
          aria-describedby={descriptionId}
          onChange={enterAddress}
          onBlur={field.onBlur}
        />
        <Autocomplete.Menu />
      </Autocomplete.Root>
      <TextInput.ErrorText role='alert'>{error?.message}</TextInput.ErrorText>
      <TextInput.HelperText>
        {!error && lookupFailed && t({ id: 'benefits.adac.form.suggestionsUnavailable' })}
      </TextInput.HelperText>
    </TextInput.Root>
  );
};
